import {
    getAfterFlipPoints,
    getAfterRotatePoints,
    getCenterWithPoints,
    getElementFrameAfterRotate,
    getFrameAllGrids, getPointsWithFrame,
} from '@src/slide/application/service/snap-line-service/util/frame';
import { OperationType } from '@src/slide/application/service/snap-line-service/interface/snap-line-service.interface';
import { RefSourceType } from '@src/slide/application/service/snap-line-service/interface/ref.interface';
import {
    EdgeType,
    OperationAnchor,
    Orient,
} from '@src/slide/application/service/snap-line-service/interface/orient.interface';
import { isCloseEqual } from '@src/slide/application/service/snap-line-service/util';
import { getMousePoint } from '@src/slide/application/service/snap-line-service/util/movement';
import { LineType } from '@src/slide/application/service/snap-line-service/interface/line.interface';
import isEqual from 'lodash/isEqual';
import type {
    IActiveSnapGridLine,
    ISnapGridLine,
} from '@src/slide/application/service/snap-line-service/interface/line.interface';
import type { IRefFrame } from '@src/slide/application/service/snap-line-service/interface/ref.interface';
import type { IPoint } from '@src/slide/infrastructure/module/independent/graphic/point/point.interface';
import type { IFrame } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';
import type {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import type { SnapLineContext } from '@src/slide/application/service/snap-line-service/context';

export enum DetectorType {
    STATIC,
    DYNAMIC,
}

export abstract class BaseDetector {
    public type: DetectorType;
    private refGridsCache: ISnapGridLine[] | undefined;

    public constructor(
        protected context: SnapLineContext,
        private disableCache = false,
    ) {}

    /**
     * 进行参考线吸附探测
     */
    public detect(): IActiveSnapGridLine[] {
        const refGrids = this.getRefGrids();
        const detectGrids = this.getDetectGrids();
        const activeSnapLines = this.getClosestSnapLineInDirections(detectGrids, refGrids);

        return this.processActiveSnapLines(activeSnapLines);
    }

    /**
     * 清除 refers 网格缓存
     */
    public clearCache() {
        this.refGridsCache = undefined;
    }

    /**
     * 获取 refer 网格化后的网格线
     */
    protected getRefGrids() {
        const isReferChanged = this.type === DetectorType.STATIC
            ? this.staticRefer.referChanged
            : this.dynamicRefer.referChanged;

        if (!this.refGridsCache || isReferChanged || this.disableCache) {
            const refGrids = this.collectRefGrids();
            this.collectRefGridsEffect(refGrids);

            this.refGridsCache = refGrids;
        }

        return this.refGridsCache;
    }

    /**
     * 收集 refer 网格线的副作用逻辑，比如有的需要额外收集等距元素
     */
    protected collectRefGridsEffect(refGrids: ISnapGridLine[]) {}

    /**
     * 生效吸附线后处理
     */
    protected processActiveSnapLines(activeSnapLines: IActiveSnapGridLine[]): IActiveSnapGridLine[] {
        return activeSnapLines;
    }

    /**
     * 是否触发吸附
     */
    protected isTriggerAdsroption(detectGrid: ISnapGridLine, refGrid: ISnapGridLine, gap: number) {
        return gap <= refGrid.maxAdsorbGap;
    }

    /**
     * 获取在同一方向触发距离最近的吸附线
     */
    protected getClosestSnapLineInDirections(
        detectGrids: ISnapGridLine[],
        refGrids: ISnapGridLine[],
    ): IActiveSnapGridLine[] {
        let activeSnapLines: IActiveSnapGridLine[] = [];

        refGrids?.forEach((refGrid) => {
            detectGrids.forEach((detectGrid) => {
                const gridGap = this.getGridGap(detectGrid.position, refGrid.position);
                if (
                    detectGrid.orient === refGrid.orient
                    && this.isTriggerAdsroption(detectGrid, refGrid, gridGap)
                ) {
                    // 在同方向上进入到吸附热区则判定为可生效的参考线
                    // 在同一方向，命中多个refGrid时，距离优先
                    const existSameOrientGridIndex = activeSnapLines.findIndex(line => line.orient === refGrid.orient);

                    const currentRefSnapGridLine: IActiveSnapGridLine = {
                        ...refGrid,
                        activeAdsorbGap: this.getGridGap(refGrid.position, detectGrid.position, false),
                        frame: this.context.detectFrame as IFrame,
                    };

                    if (existSameOrientGridIndex === -1) {
                        activeSnapLines.push(currentRefSnapGridLine);
                        return;
                    }

                    const sameOrientGrid = activeSnapLines[existSameOrientGridIndex];

                    // 如果两个 refGrid 近似相等的，但是非同一个 refGrid，并且触发吸附的距离相等，就一起触发
                    if (
                        !isEqual(currentRefSnapGridLine, sameOrientGrid)
                        && isCloseEqual(sameOrientGrid.activeAdsorbGap, currentRefSnapGridLine.activeAdsorbGap)
                    ) {
                        activeSnapLines.push(currentRefSnapGridLine);
                        return;
                    }

                    // 位置不一致，就取离操作元素更近的网格线
                    const isCurrentGridCloser = Math.abs(currentRefSnapGridLine.activeAdsorbGap)
                        < Math.abs(sameOrientGrid.activeAdsorbGap);

                    if (isCurrentGridCloser) {
                        // 同方向的activeSnapLines 距离都相等。如果有更新的，就要吧将同方向的都过滤掉，用更近的代替
                        activeSnapLines = activeSnapLines
                            .filter(snapLine => snapLine.orient !== sameOrientGrid.orient);
                        activeSnapLines.push(currentRefSnapGridLine);
                    }
                }
            });
        });

        return activeSnapLines;
    }

    /**
     * 获取进行探测的网格线，由操作元素转化
     */
    protected getDetectGrids(): ISnapGridLine[] {
        const { operationType } = this.context;
        const gridInitialOptions = operationType === OperationType.MOVE
            ? this.getMoveModeDetectElementGridOptions() : this.getResizeModeDetectElementGridOptions();

        const selectElement = this.getSelectElement();
        return gridInitialOptions.map(({ orient, position, type, fromEdge }): ISnapGridLine => ({
            orient,
            position,
            type,
            fromEdge,
            maxAdsorbGap: this.context.option.maxAdsorbGap,
            source: RefSourceType.ELEMENT,
            refElements: [{
                ele: selectElement,
                source: RefSourceType.ELEMENT,
                outerFrameOnSlide: getElementFrameAfterRotate(selectElement),
            }],
        }));
    }

    /**
     * 获取 move 下，探测元素转换的网格配置
     */
    protected getMoveModeDetectElementGridOptions() {
        const { detectFrame } = this.context;
        const {
            topGrid, horizontalBaseLineGrid, bottomGrid,
            leftGrid, verticalBaseLineGrid, rightGrid } = getFrameAllGrids(detectFrame as IFrame);

        return [
            topGrid,
            horizontalBaseLineGrid,
            bottomGrid,
            leftGrid,
            verticalBaseLineGrid,
            rightGrid,
        ];
    }

    /**
     * 获取 resize 下，探测元素转换的网格配置
     */
    protected getResizeModeDetectElementGridOptions() {
        const { originOperationAnchor } = this.context;

        const { rotate = 0 } = this.context.nextSizeOnSlide;
        const isOperateEdgeCenterAnchor = originOperationAnchor ===  OperationAnchor.LEFT
            || originOperationAnchor === OperationAnchor.TOP
            || originOperationAnchor === OperationAnchor.BOTTOM
            || originOperationAnchor === OperationAnchor.RIGHT;

        // 元素frame不正的情况下（自身结合父元素一起，旋转角度非 90的倍数），拖动非四角锚点的锚点，不进行吸附
        if (rotate % 90 !== 0 && isOperateEdgeCenterAnchor) {
            return [];
        }

        // 操作边中间的锚点的时候，按边缘生成探测网格线
        if (isOperateEdgeCenterAnchor) {
            return this.getGridOptionsByEdge();
        }

        if (this.context.noChangeAspect) {
            return this.getGridOptionsByNoChangeAspect();
        }

        // 操作四角的时候，在角出生成十字探测网格线
        return this.getGridOptionsByAnchor();
    }

    protected getSelectElement(): IElementBaseModel {
        return this.context.selectionService.getSelectedModel() as IElementBaseModel;
    }

    protected getGridGap(srcPos: number, destPos: number, abs = true): number {
        const gap = srcPos - destPos;
        return abs ? Math.abs(gap) : gap;
    }

    protected getRefFrame(frame: IFrame): IRefFrame {
        return {
            ...frame,
            right: frame.left + frame.width,
            bottom: frame.top + frame.height,
        };
    }

    protected get staticRefer() {
        return this.context.staticRefer;
    }
    protected get dynamicRefer() {
        return this.context.dynamicRefer;
    }
    protected get staticReferGrids() {
        return this.staticRefer.refGrids;
    }

    /**
     * 在resizeNode 锚点处生成十字探测网格线
     */
    protected getGridOptionsByAnchor() {
        const { operationAnchorOnSlide } = this.context;
        const anchorPoint = this.getResizeNodePositionOnSlide();
        const isAnchorLeftSide = operationAnchorOnSlide.includes('left');
        const isAnchorTopSide = operationAnchorOnSlide.includes('top');
        const verticalGrid = {
            orient: Orient.VERTICAL,
            position: anchorPoint.x,
            type: LineType.EDGE,
            // operationAnchorOnSlide 是根据锚点与中心的向量计算出的带方位的锚点
            // 如果带有 left，说明在锚点在偏左侧，则映射为从左衍生出的网格，元素的右边才能吸附上。下同
            fromEdge: isAnchorLeftSide ? EdgeType.LEFT : EdgeType.RIGHT,
        };
        const horizontalGrid = {
            orient: Orient.HORIZONTAL,
            position: anchorPoint.y,
            type: LineType.EDGE,
            fromEdge: isAnchorTopSide ? EdgeType.TOP : EdgeType.BOTTOM,
        };

        return [verticalGrid, horizontalGrid];
    }

    /**
     * 获取锚点在 slide 画布上的位置
     */
    protected getResizeNodePositionOnSlide(): IPoint {
        const mouseStartPointOnWindow = this.context.startMousePoint;
        const currentPointOnWindow = getMousePoint(this.context.mouseEvent);
        const windowZoom = 1;
        const offset = {
            x: (currentPointOnWindow.x - mouseStartPointOnWindow.x) / this.context.scale / windowZoom,
            y: (currentPointOnWindow.y - mouseStartPointOnWindow.y) / this.context.scale / windowZoom,
        };

        const elementFrameOnSlide = this.context.element.getFrame().frameOnSlide();
        const originPoints = getPointsWithFrame(elementFrameOnSlide);
        const { relativeFlipH, relativeFlipV } = this.context.relativeProps;
        const afterFlipPoint = getAfterFlipPoints(
            originPoints,
            getCenterWithPoints(originPoints),
            !!relativeFlipV,
            !!relativeFlipH,
        );
        const afterRotatePoints = getAfterRotatePoints(afterFlipPoint, this.context.rotateOnSlide);
        // afterRotatePoints是按正常 frame的四个点的顺序，翻转、旋转后的四个点的位置
        // 通过originOperationAnchor，获取到正在操作的锚点是四个点中的哪一个，进而获取到锚点旋转后的位置
        const anchorIndex = [
            OperationAnchor.LEFT_TOP, OperationAnchor.RIGHT_TOP,
            OperationAnchor.RIGHT_BOTTOM, OperationAnchor.LEFT_BOTTOM,
        ].indexOf(this.context.originOperationAnchor);
        const anchorPoint = afterRotatePoints[anchorIndex];

        return {
            x: anchorPoint.x + offset.x,
            y: anchorPoint.y + offset.y,
        };
    }

    /**
     * 在 frame 四边的位置生成探测网格线
     */
    private getGridOptionsByEdge() {
        const { detectFrame, operationAnchorOnSlide } = this.context;
        const {
            topGrid, bottomGrid,
            leftGrid, rightGrid,
        } = getFrameAllGrids(detectFrame);

        let gridInitialOptions: any = [];
        switch (operationAnchorOnSlide) {
            case OperationAnchor.LEFT:
                gridInitialOptions = [leftGrid];
                break;
            case OperationAnchor.RIGHT:
                gridInitialOptions = [rightGrid];
                break;
            case OperationAnchor.TOP:
                gridInitialOptions = [topGrid];
                break;
            case OperationAnchor.BOTTOM:
                gridInitialOptions = [bottomGrid];
                break;
        }

        return gridInitialOptions;
    }

    private getGridOptionsByNoChangeAspect() {
        const { detectFrame, operationAnchorOnSlide } = this.context;
        const {
            topGrid, bottomGrid,
            leftGrid, rightGrid,
        } = getFrameAllGrids(detectFrame);

        let gridInitialOptions: any = [];
        switch (operationAnchorOnSlide) {
            case OperationAnchor.LEFT_TOP:
                gridInitialOptions = [leftGrid, topGrid];
                break;
            case OperationAnchor.RIGHT_TOP:
                gridInitialOptions = [rightGrid, topGrid];
                break;
            case OperationAnchor.LEFT_BOTTOM:
                gridInitialOptions = [leftGrid, bottomGrid];
                break;
            case OperationAnchor.RIGHT_BOTTOM:
                gridInitialOptions = [rightGrid, bottomGrid];
                break;
        }

        return gridInitialOptions;
    }

    /**
     * 获取按一定规则，将参照物转化的网格线，等待吸附检测
     */
    protected abstract collectRefGrids(): ISnapGridLine[];
}
